import {ButtonGroup, Dialog, Infobox, showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useUploadFileMutation} from '@dms/api/base';
import {useSignRecordAuditMutation} from '@dms/api/core';
import i18n from '@dms/i18n';

import {ApiError} from 'shared/web';

import {handleApiError} from '../../utils/handleApiError';
import {SignaturePadHandle} from './SignaturePad';
import {SignerBlock} from './SignerBlock';

interface SignRecordAuditModalProps {
  auditId: string;
  auditorName: string | null;
  responsiblePersonName: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const blobToFile = (blob: Blob, filename: string) =>
  new File([blob], filename, {type: blob.type || 'image/png'});

// "Auditor — Radek Ďuriš"; role label alone when the person is not assigned/resolved yet.
const buildSignerHeading = (roleLabel: string, name: string | null) =>
  isNilOrEmpty(name) ? roleLabel : `${roleLabel} — ${name}`;

export function SignRecordAuditModal(props: SignRecordAuditModalProps) {
  const auditorPadRef = useRef<SignaturePadHandle>(null);
  const responsiblePadRef = useRef<SignaturePadHandle>(null);

  const [uploadFile] = useUploadFileMutation();
  const [signAudit] = useSignRecordAuditMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [auditorSignatureError, setAuditorSignatureError] = useState<string | null>(null);
  const [responsibleSignatureError, setResponsibleSignatureError] = useState<string | null>(null);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    auditorPadRef.current?.clear();
    responsiblePadRef.current?.clear();
    setAuditorSignatureError(null);
    setResponsibleSignatureError(null);
    props.onClose();
  };

  const handleClearAuditor = () => {
    auditorPadRef.current?.clear();
    setAuditorSignatureError(null);
  };

  const handleClearResponsible = () => {
    responsiblePadRef.current?.clear();
    setResponsibleSignatureError(null);
  };

  const handleSubmit = async () => {
    const auditorEmpty = auditorPadRef.current?.isEmpty() ?? true;
    const responsibleEmpty = responsiblePadRef.current?.isEmpty() ?? true;

    setAuditorSignatureError(
      auditorEmpty ? i18n.t('page.recordAudit.signModal.errors.signatureRequired') : null
    );
    setResponsibleSignatureError(
      responsibleEmpty ? i18n.t('page.recordAudit.signModal.errors.signatureRequired') : null
    );

    if (auditorEmpty || responsibleEmpty) {
      return;
    }

    setIsSubmitting(true);

    const [auditorBlob, responsibleBlob] = await Promise.all([
      auditorPadRef.current?.getBlob() ?? Promise.resolve(null),
      responsiblePadRef.current?.getBlob() ?? Promise.resolve(null),
    ]);

    if (isNil(auditorBlob) || isNil(responsibleBlob)) {
      showNotification.error();
      setIsSubmitting(false);
      return;
    }

    Promise.all([
      uploadFile({file: blobToFile(auditorBlob, 'audit-signature-auditor.png')}).unwrap(),
      uploadFile({
        file: blobToFile(responsibleBlob, 'audit-signature-responsible.png'),
      }).unwrap(),
    ])
      .then(([auditorUpload, responsibleUpload]) =>
        signAudit({
          auditId: props.auditId,
          body: {
            auditorSignatureFileId: auditorUpload.fileId,
            responsibleSignatureFileId: responsibleUpload.fileId,
          },
        }).unwrap()
      )
      // No success toast: the modal closes and the header SIGNED state already reflects the result.
      .then(() => {
        handleClose();
      })
      .catch((error: ApiError) => handleApiError(error))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={handleClose}
      title={i18n.t('page.recordAudit.signModal.title')}
      size="default"
      scrollBehavior="inside"
    >
      <VStack spacing={4}>
        <Infobox variant="info" data={[{value: i18n.t('page.recordAudit.signModal.notice')}]} />

        <SignerBlock
          ref={auditorPadRef}
          heading={buildSignerHeading(
            i18n.t('page.recordAudit.signModal.auditor'),
            props.auditorName
          )}
          signatureError={auditorSignatureError}
          isSubmitting={isSubmitting}
          onClear={handleClearAuditor}
        />
        <SignerBlock
          ref={responsiblePadRef}
          heading={buildSignerHeading(
            i18n.t('page.recordAudit.signModal.responsible'),
            props.responsiblePersonName
          )}
          signatureError={responsibleSignatureError}
          isSubmitting={isSubmitting}
          onClear={handleClearResponsible}
        />

        <ButtonGroup
          align="right"
          buttons={[
            {
              title: i18n.t('general.actions.cancel'),
              variant: 'secondary',
              onClick: handleClose,
              isDisabled: isSubmitting,
            },
            {
              title: i18n.t('general.actions.confirm'),
              variant: 'primary',
              onClick: handleSubmit,
              leftIcon: 'action/check_circle',
              isLoading: isSubmitting,
              isDisabled: isSubmitting,
            },
          ]}
        />
      </VStack>
    </Dialog>
  );
}
